import { type RootState, store } from "@store/index";

import { type AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import { type LayoutProps } from "skedyul-ui/ui";
import { close as closeDialog } from "@store/features/layout/dialogSlice";
import {
  generateDialogFromSchema,
  getDialogParams,
} from "../../schemas/dialog.schema";
import { close as closeSideNavigation } from "@store/features/layout/sideNavigationSlice";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { useTheme } from "@teispace/next-themes";

type ThemeContext = ReturnType<typeof useTheme>;
import { generateSideNavigationComponent } from "@schemas/sideNavigation.schema";
import {
  generateChatListSchema,
  generateInputBarSchema,
  generateStagedMessageSchema,
  generateSuggestedResponseSchema,
  type ModelOption,
} from "@schemas/chat.schema";
/* eslint-disable */

export type AppLayoutState = {
  chat: RootState["chat"];
  workplace: RootState["workplace"];
  sideNavigation: RootState["sideNavigation"];
  dialog: RootState["dialog"];
  workflows: RootState["workflows"];
  agents: RootState["agents"];
};
/* eslint-enable */

dayjs.extend(relativeTime);

type SideNavigationContent = ReturnType<typeof generateSideNavigationComponent>;

const emptySideNavigation = (
  position: "left" | "right",
  width: string,
): SideNavigationContent => ({
  side: position,
  isOpen: false,
  width,
  component: {
    component: "Custom",
    props: { children: null },
  },
});

const safeGenerateSideNavigation = (
  args: Parameters<typeof generateSideNavigationComponent>[0],
): SideNavigationContent => {
  try {
    return generateSideNavigationComponent(args);
  } catch (error) {
    console.error("Failed to generate side navigation:", error);
    return emptySideNavigation(args.position, args.width);
  }
};

// New helper function to generate dialog content
export const getDialogContent = async (
  layoutState: AppLayoutState,
  fullState: RootState,
  dialog: AppLayoutState["dialog"],
  t: unknown,
  router: AppRouterInstance,
) => {
  try {
    return await generateDialogFromSchema(
      dialog.schema,
      getDialogParams(fullState, dialog.params || {}),
      router,
    );
  } catch (error) {
    console.error("Failed to generate dialog content:", error);
    return {
      sections: [],
      title: "Error",
      description: "Failed to load dialog content",
      actions: [],
    };
  }
};

export const transformAppStateToLayoutContent = (
  appState: AppLayoutState,
  fullState: RootState,
  viewPortIsMobile: boolean,
  themeProvider: ThemeContext,
  t: unknown,
  {
    pathname,
    params,
    router,
  }: {
    params: { subdomain: string };
    pathname: string | null;
    router: AppRouterInstance;
  },
  chatWindow: {
    view: "open" | "collapsed" | "hidden";
    contactId: string;
    handleChange: React.Dispatch<
      React.SetStateAction<{
        view: "open" | "collapsed" | "hidden";
        contactId: string;
      }>
    >;
  },
  llmModels: ModelOption[] = [],
  onModelPersist?: (modelId: string) => void,
): LayoutProps => {
  const {
    chat: {
      contact: {
        data: contact,
        isLoading: contactLoading,
        newMessage,
        stagedMessage,
        suggestedResponse,
      },
    },
    workplace: {
      currentMember: { data: currentMember },
      workplace: { data: workplaceData },
    },
    dialog,
    sideNavigation: { left, right },
  } = appState;

  const { view, contactId, handleChange } = chatWindow;

  const selectedContact = appState.chat.contact.data ?? undefined;

  // get the url for the selected contact try to get the url for the instance then falls back to the conversations page
  const selectedContactUrl = selectedContact?.contactAssociations[0]
    ? `/${params.subdomain}/${selectedContact.contactAssociations[0].instance.model.handlePlural}/${selectedContact.contactAssociations[0].instance.id}`
    : `/${params.subdomain}/conversations/${contactId}`;

  const selectedSubscription = selectedContact
    ? selectedContact.communicationChannelSubscriptions.find(
        (s) => s.selected,
      ) || selectedContact.communicationChannelSubscriptions[0]
    : undefined;

  const messages = contact?.messages || [];
  const lastMessage = messages.length ? messages[messages.length - 1] : null;
  const activeTime = (() => {
    const lc = (
      selectedContact as unknown as { lastContactedAt?: string | null }
    )?.lastContactedAt;
    if (lc) return dayjs(lc).format("D MMM YYYY HH:mm");
    return lastMessage
      ? dayjs(lastMessage.createdAt).format("D MMM YYYY HH:mm")
      : "";
  })();

  return {
    children: [],
    dialog: {
      open: dialog.open,
      handleClose: () => {
        store.dispatch(closeDialog());
      },
      sections: [],
      title: "",
      description: "",
      actions: [],
    },
    sideNavigation: {
      left: safeGenerateSideNavigation({
        position: "left",
        width: left.width,
        viewPortIsMobile,
        themeContext: themeProvider,
        routerContext: {
          router,
          params,
          pathname: pathname || "",
        },
        state: fullState,
        props: left.params,
      }),
      right: safeGenerateSideNavigation({
        position: "right",
        width: right.width,
        viewPortIsMobile,
        themeContext: themeProvider,
        routerContext: {
          router,
          params,
          pathname: pathname || "",
        },
        state: fullState,
        props: right.params,
      }),
    },
    chatWindow: {
      title: "window",
      view: view,
      isLoading: contactLoading,
      height: 600,
      size: "sm",
      chatTopBar: {
        activeTime,
        avatar: (() => {
          type ImageMeta = { url?: string; seed?: string } | null | undefined;
          type AssocInstance = {
            _meta?: { label?: string; image?: ImageMeta };
          };
          type ContactAssociation = { instance?: AssocInstance };
          type Contact = {
            id?: unknown;
            name?: unknown;
            contactAssociations?: unknown[];
          };

          const contact = (selectedContact || {}) as Contact;
          const firstAssoc: unknown = Array.isArray(contact.contactAssociations)
            ? contact.contactAssociations[0]
            : undefined;
          const assoc =
            firstAssoc && typeof firstAssoc === "object"
              ? (firstAssoc as ContactAssociation)
              : undefined;
          const instance = assoc?.instance;
          const meta = instance?._meta;

          const fallbackId = typeof contact.id === "string" ? contact.id : "";
          const fallbackName =
            typeof contact.name === "string" ? contact.name : undefined;

          const name =
            (typeof meta?.label === "string" && meta.label) ||
            fallbackName ||
            fallbackId ||
            "";

          const image = meta?.image;
          const url =
            image && "url" in (image || {}) && typeof image.url === "string"
              ? image.url
              : undefined;
          const seed =
            image && "seed" in (image || {}) && typeof image.seed === "string"
              ? image.seed
              : fallbackId;

          return url ? { name, url } : { name, seed };
        })(),
        isLoading: contactLoading,
        view: view,
        handleViewChange: (view: "open" | "collapsed" | "hidden") => {
          handleChange({ view, contactId });
        },
        href: selectedContactUrl,
        chips: [],
        actions: [
          {
            id: "collapse",
            contentPosition: "center",
            variant: "ghost",
            size: "icon",
            leftIcon: view === "collapsed" ? "ChevronUp" : "ChevronDown",
            className: "",
            handleClick: () => {
              if (view === "collapsed") {
                handleChange({ view: "open", contactId });
              } else {
                handleChange({ view: "collapsed", contactId });
                // hide right side navigation
                store.dispatch(closeSideNavigation({ position: "right" }));
              }
            },
          },
          {
            id: "expand",
            variant: "ghost",
            size: "icon",
            leftIcon: "MoveDiagonal2",
            handleClick: () => {
              router.push(`/${params?.subdomain}/conversations/${contactId}`);
              handleChange({ view: "hidden", contactId });
              // hide right side navigation
              store.dispatch(closeSideNavigation({ position: "right" }));
            },
          },
          {
            id: "close",
            variant: "ghost",
            size: "icon",
            leftIcon: "X",
            handleClick: () => {
              handleChange({ view: "hidden", contactId });
              // hide right side navigation
              store.dispatch(closeSideNavigation({ position: "right" }));
            },
          },
        ],
      },
      topAlert: (() => {
        const subscriptionOptIn = (
          selectedSubscription as unknown as { optIn?: string }
        )?.optIn;
        const contactOptIn = (selectedContact as unknown as { optIn?: string })
          ?.optIn;
        const isOptedOut =
          subscriptionOptIn === "OPT_OUT" || contactOptIn === "OPT_OUT";
        if (!isOptedOut) return undefined;
        const contactName = (() => {
          const assoc = selectedContact?.contactAssociations?.[0];
          const label = assoc?.instance?._meta?.label;
          return (
            (typeof label === "string" && label) ||
            selectedContact?.id ||
            "Contact"
          );
        })();
        return {
          title: "Opted Out",
          description: `${contactName} has Opt'd Out From ${selectedSubscription?.channel?.name || ""}`,
          variant: "destructive",
        } as const;
      })(),
      chatList: generateChatListSchema(
        {
          contactId,
          isLoading: contactLoading,
          messages,
          fetchedAt: undefined,
          selectedContact,
        },
        router,
      ),
      chatInputBar: generateInputBarSchema({
        selectedSubscription,
        isSending: !!newMessage?.isSending,
        isLoading: !!newMessage?.isLoading,
        currentMember: currentMember || undefined,
        selectedContact,
        newMessage: {
          message: newMessage?.data?.message || "",
          id: newMessage?.data?.id || "",
          resetKey: newMessage?.resetKey,
        },
      }),
      stagedMessage: generateStagedMessageSchema({
        message: stagedMessage?.message,
        isVisible: stagedMessage?.isVisible,
        isLoading: stagedMessage?.isLoading,
        currentMember: currentMember || undefined,
        selectedContact,
        selectedSubscription,
      }),
      suggestedResponse: workplaceData?.agentPersona
        ? generateSuggestedResponseSchema({
            message: suggestedResponse?.message || "",
            isVisible: suggestedResponse?.isVisible || false,
            isLoading: suggestedResponse?.isLoading || false,
            selectedModelId: suggestedResponse?.selectedModelId || null,
            error: suggestedResponse?.error || null,
            models: llmModels,
            currentMember: currentMember || undefined,
            selectedContact,
            selectedSubscription,
            messages,
            onModelPersist,
          })
        : undefined,
    },
  };
};
